import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import settingsService from '../../services/settingsService';
// TODO: Uncomment when Security functionality is re-enabled
// import { TwoFactorSettings } from '../../components/settings';
import './SettingsPage.css';

function SettingsPage() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('shipping'); // Default to 'shipping' since 'general' is hidden
  const [formData, setFormData] = useState({});

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await settingsService.getSettings();
        setSettings(response.data.data);
        setFormData(response.data.data);
      } catch (err) {
        toast.error('Failed to load settings');
        console.error('Error fetching settings:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleInputChange = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const handleAddressChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      general: {
        ...prev.general,
        storeAddress: {
          ...prev.general.storeAddress,
          [field]: value,
        },
      },
    }));
  };

  const handleBusinessHoursChange = (day, field, value) => {
    setFormData((prev) => ({
      ...prev,
      contact: {
        ...prev.contact,
        businessHours: {
          ...prev.contact.businessHours,
          [day]: {
            ...prev.contact.businessHours[day],
            [field]: value,
          },
        },
      },
    }));
  };

  const handleSocialMediaChange = (platform, value) => {
    setFormData((prev) => ({
      ...prev,
      contact: {
        ...prev.contact,
        socialMedia: {
          ...prev.contact.socialMedia,
          [platform]: value,
        },
      },
    }));
  };

  const handleContactFormChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      contact: {
        ...prev.contact,
        contactForm: {
          ...prev.contact.contactForm,
          [field]: value,
        },
      },
    }));
  };

  const handleAdditionalContactsChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      contact: {
        ...prev.contact,
        additionalContacts: {
          ...prev.contact.additionalContacts,
          [field]: value,
        },
      },
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await settingsService.updateSettings(formData);
      setSettings(formData);
      toast.success('Settings saved successfully');
    } catch (err) {
      toast.error('Failed to save settings');
      console.error('Error saving settings:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setFormData(settings);
    toast.info('Changes discarded');
  };

  const hasChanges = JSON.stringify(settings) !== JSON.stringify(formData);

  if (isLoading) {
    return (
      <div className="settings-page">
        <div className="page-loading">
          <div className="loading-spinner"></div>
          <span>Loading settings...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Settings</h1>
          <p>Configure your store settings and preferences.</p>
        </div>
        {hasChanges && (
          <div className="page-header-actions">
            <button className="btn btn-secondary" onClick={handleReset} disabled={isSaving}>
              Discard
            </button>
            <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>

      <div className="settings-layout">
        {/* Tabs */}
        <div className="settings-tabs">
          {/* TODO: Uncomment when General functionality is fully implemented */}
          {/* <button
            className={`tab ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.343 3.94c.09-.542.56-.94 1.11-.94h1.093c.55 0 1.02.398 1.11.94l.149.894c.07.424.384.764.78.93.398.164.855.142 1.205-.108l.737-.527a1.125 1.125 0 011.45.12l.773.774c.39.389.44 1.002.12 1.45l-.527.737c-.25.35-.272.806-.107 1.204.165.397.505.71.93.78l.893.15c.543.09.94.56.94 1.109v1.094c0 .55-.397 1.02-.94 1.11l-.893.149c-.425.07-.765.383-.93.78-.165.398-.143.854.107 1.204l.527.738c.32.447.269 1.06-.12 1.45l-.774.773a1.125 1.125 0 01-1.449.12l-.738-.527c-.35-.25-.806-.272-1.203-.107-.397.165-.71.505-.781.929l-.149.894c-.09.542-.56.94-1.11.94h-1.094c-.55 0-1.019-.398-1.11-.94l-.148-.894c-.071-.424-.384-.764-.781-.93-.398-.164-.854-.142-1.204.108l-.738.527c-.447.32-1.06.269-1.45-.12l-.773-.774a1.125 1.125 0 01-.12-1.45l.527-.737c.25-.35.273-.806.108-1.204-.165-.397-.505-.71-.93-.78l-.894-.15c-.542-.09-.94-.56-.94-1.109v-1.094c0-.55.398-1.02.94-1.11l.894-.149c.424-.07.765-.383.93-.78.165-.398.143-.854-.107-1.204l-.527-.738a1.125 1.125 0 01.12-1.45l.773-.773a1.125 1.125 0 011.45-.12l.737.527c.35.25.807.272 1.204.107.397-.165.71-.505.78-.929l.15-.894z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            General
          </button> */}
          <button
            className={`tab ${activeTab === 'shipping' ? 'active' : ''}`}
            onClick={() => setActiveTab('shipping')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
            </svg>
            Shipping
          </button>
          <button
            className={`tab ${activeTab === 'tax' ? 'active' : ''}`}
            onClick={() => setActiveTab('tax')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
            </svg>
            Tax
          </button>
          {/* TODO: Uncomment when Notifications functionality is fully implemented */}
          {/* <button
            className={`tab ${activeTab === 'notifications' ? 'active' : ''}`}
            onClick={() => setActiveTab('notifications')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
            </svg>
            Notifications
          </button> */}
          {/* TODO: Uncomment when Contact functionality is fully implemented */}
          {/* <button
            className={`tab ${activeTab === 'contact' ? 'active' : ''}`}
            onClick={() => setActiveTab('contact')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
            </svg>
            Contact
          </button> */}
          {/* TODO: Uncomment when Security functionality is fully implemented */}
          {/* <button
            className={`tab ${activeTab === 'security' ? 'active' : ''}`}
            onClick={() => setActiveTab('security')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
            </svg>
            Security
          </button> */}
          <button
            className="tab"
            onClick={() => navigate('/settings/users')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
            </svg>
            Admin Users
          </button>
        </div>

        {/* Content */}
        <div className="settings-content">
          {/* TODO: Uncomment when General functionality is fully implemented */}
          {/* {activeTab === 'general' && formData.general && (
            <div className="settings-section">
              <h2>General Settings</h2>
              <p className="section-description">Basic information about your store.</p>

              <div className="form-group">
                <label className="form-label">Store Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.general.storeName}
                  onChange={(e) => handleInputChange('general', 'storeName', e.target.value)}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Contact Email</label>
                  <input
                    type="email"
                    className="form-input"
                    value={formData.general.storeEmail}
                    onChange={(e) => handleInputChange('general', 'storeEmail', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Contact Phone</label>
                  <input
                    type="tel"
                    className="form-input"
                    value={formData.general.storePhone}
                    onChange={(e) => handleInputChange('general', 'storePhone', e.target.value)}
                  />
                </div>
              </div>

              <h3>Store Address</h3>
              <div className="form-group">
                <label className="form-label">Address Line 1</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.general.storeAddress.line1}
                  onChange={(e) => handleAddressChange('line1', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Address Line 2</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.general.storeAddress.line2 || ''}
                  onChange={(e) => handleAddressChange('line2', e.target.value)}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">City</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.general.storeAddress.city}
                    onChange={(e) => handleAddressChange('city', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Postcode</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.general.storeAddress.postcode}
                    onChange={(e) => handleAddressChange('postcode', e.target.value)}
                  />
                </div>
              </div>

              <h3>Regional Settings</h3>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Currency</label>
                  <select
                    className="form-select"
                    value={formData.general.currency}
                    onChange={(e) => handleInputChange('general', 'currency', e.target.value)}
                  >
                    <option value="GBP">GBP (£)</option>
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Timezone</label>
                  <select
                    className="form-select"
                    value={formData.general.timezone}
                    onChange={(e) => handleInputChange('general', 'timezone', e.target.value)}
                  >
                    <option value="Europe/London">Europe/London</option>
                    <option value="America/New_York">America/New York</option>
                    <option value="America/Los_Angeles">America/Los Angeles</option>
                  </select>
                </div>
              </div>
            </div>
          )} */}

          {activeTab === 'shipping' && formData.shipping && (
            <div className="settings-section">
              <h2>Shipping Settings</h2>
              <p className="section-description">Configure shipping costs by region.</p>

              <h3>Shipping Costs by Region</h3>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">UK Shipping (£)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-input"
                    value={formData.shipping.shippingCosts?.uk || 0}
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        shipping: {
                          ...prev.shipping,
                          shippingCosts: {
                            ...prev.shipping.shippingCosts,
                            uk: parseFloat(e.target.value) || 0
                          }
                        }
                      }));
                    }}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">EU Shipping (£)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-input"
                    value={formData.shipping.shippingCosts?.eu || 0}
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        shipping: {
                          ...prev.shipping,
                          shippingCosts: {
                            ...prev.shipping.shippingCosts,
                            eu: parseFloat(e.target.value) || 0
                          }
                        }
                      }));
                    }}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">US Shipping (£)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-input"
                    value={formData.shipping.shippingCosts?.us || 0}
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        shipping: {
                          ...prev.shipping,
                          shippingCosts: {
                            ...prev.shipping.shippingCosts,
                            us: parseFloat(e.target.value) || 0
                          }
                        }
                      }));
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'tax' && formData.tax && (
            <div className="settings-section">
              <h2>Tax & Payment Settings</h2>
              <p className="section-description">Configure currency and tax rates by region.</p>

              <h3>Currency</h3>
              <div className="form-group">
                <label className="form-label">Store Currency</label>
                <select
                  className="form-select"
                  value={formData.general?.currency || 'GBP'}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      general: {
                        ...prev.general,
                        currency: e.target.value
                      }
                    }));
                  }}
                >
                  <option value="GBP">GBP (£)</option>
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                </select>
              </div>

              <h3>Tax Rates by Region</h3>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">UK Tax Rate (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-input"
                    value={formData.tax.taxRates?.uk || 0}
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        tax: {
                          ...prev.tax,
                          taxRates: {
                            ...prev.tax.taxRates,
                            uk: parseFloat(e.target.value) || 0
                          }
                        }
                      }));
                    }}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">EU Tax Rate (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-input"
                    value={formData.tax.taxRates?.eu || 0}
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        tax: {
                          ...prev.tax,
                          taxRates: {
                            ...prev.tax.taxRates,
                            eu: parseFloat(e.target.value) || 0
                          }
                        }
                      }));
                    }}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">US Tax Rate (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-input"
                    value={formData.tax.taxRates?.us || 0}
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        tax: {
                          ...prev.tax,
                          taxRates: {
                            ...prev.tax.taxRates,
                            us: parseFloat(e.target.value) || 0
                          }
                        }
                      }));
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* TODO: Uncomment when Notifications functionality is fully implemented */}
          {/* {activeTab === 'notifications' && formData.notifications && (
            <div className="settings-section">
              <h2>Notification Settings</h2>
              <p className="section-description">Configure email notifications and alerts.</p>

              <h3>Order Notifications</h3>
              <div className="notifications-grid">
                <div className="form-group">
                  <label className="form-checkbox">
                    <input
                      type="checkbox"
                      checked={formData.notifications.orderConfirmation}
                      onChange={(e) => handleInputChange('notifications', 'orderConfirmation', e.target.checked)}
                    />
                    <span>Order confirmation emails</span>
                  </label>
                </div>
                <div className="form-group">
                  <label className="form-checkbox">
                    <input
                      type="checkbox"
                      checked={formData.notifications.orderShipped}
                      onChange={(e) => handleInputChange('notifications', 'orderShipped', e.target.checked)}
                    />
                    <span>Shipping notification emails</span>
                  </label>
                </div>
                <div className="form-group">
                  <label className="form-checkbox">
                    <input
                      type="checkbox"
                      checked={formData.notifications.orderDelivered}
                      onChange={(e) => handleInputChange('notifications', 'orderDelivered', e.target.checked)}
                    />
                    <span>Delivery confirmation emails</span>
                  </label>
                </div>
              </div>

              <h3>Admin Alerts</h3>
              <div className="form-group">
                <label className="form-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.notifications.lowStockAlert}
                    onChange={(e) => handleInputChange('notifications', 'lowStockAlert', e.target.checked)}
                  />
                  <span>Low stock alerts</span>
                </label>
              </div>
              {formData.notifications.lowStockAlert && (
                <div className="form-group indented">
                  <label className="form-label">Low stock threshold</label>
                  <input
                    type="number"
                    className="form-input small"
                    value={formData.notifications.lowStockThreshold}
                    onChange={(e) => handleInputChange('notifications', 'lowStockThreshold', parseInt(e.target.value))}
                  />
                </div>
              )}
              <div className="form-group">
                <label className="form-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.notifications.newReviewAlert}
                    onChange={(e) => handleInputChange('notifications', 'newReviewAlert', e.target.checked)}
                  />
                  <span>New review notifications</span>
                </label>
              </div>

              <h3>Reports</h3>
              <div className="notifications-grid">
                <div className="form-group">
                  <label className="form-checkbox">
                    <input
                      type="checkbox"
                      checked={formData.notifications.dailySalesReport}
                      onChange={(e) => handleInputChange('notifications', 'dailySalesReport', e.target.checked)}
                    />
                    <span>Daily sales report</span>
                  </label>
                </div>
                <div className="form-group">
                  <label className="form-checkbox">
                    <input
                      type="checkbox"
                      checked={formData.notifications.weeklySalesReport}
                      onChange={(e) => handleInputChange('notifications', 'weeklySalesReport', e.target.checked)}
                    />
                    <span>Weekly sales report</span>
                  </label>
                </div>
              </div>
            </div>
          )} */}

          {/* TODO: Uncomment when Contact functionality is fully implemented */}
          {/* {activeTab === 'contact' && formData.contact && (
            <div className="settings-section">
              <h2>Contact Settings</h2>
              <p className="section-description">Configure contact information and business hours for your store.</p>

              <h3>Business Hours</h3>
              <div className="business-hours-grid">
                {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                  <div key={day} className="business-hours-row">
                    <div className="day-name">{day.charAt(0).toUpperCase() + day.slice(1)}</div>
                    <div className="hours-inputs">
                      <label className="form-checkbox compact">
                        <input
                          type="checkbox"
                          checked={formData.contact.businessHours[day].closed}
                          onChange={(e) => handleBusinessHoursChange(day, 'closed', e.target.checked)}
                        />
                        <span>Closed</span>
                      </label>
                      {!formData.contact.businessHours[day].closed && (
                        <>
                          <input
                            type="time"
                            className="form-input time-input"
                            value={formData.contact.businessHours[day].open}
                            onChange={(e) => handleBusinessHoursChange(day, 'open', e.target.value)}
                          />
                          <span className="time-separator">to</span>
                          <input
                            type="time"
                            className="form-input time-input"
                            value={formData.contact.businessHours[day].close}
                            onChange={(e) => handleBusinessHoursChange(day, 'close', e.target.value)}
                          />
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <h3>Social Media Links</h3>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Facebook</label>
                  <input
                    type="url"
                    className="form-input"
                    placeholder="https://facebook.com/yourpage"
                    value={formData.contact.socialMedia.facebook}
                    onChange={(e) => handleSocialMediaChange('facebook', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Twitter / X</label>
                  <input
                    type="url"
                    className="form-input"
                    placeholder="https://twitter.com/yourhandle"
                    value={formData.contact.socialMedia.twitter}
                    onChange={(e) => handleSocialMediaChange('twitter', e.target.value)}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Instagram</label>
                  <input
                    type="url"
                    className="form-input"
                    placeholder="https://instagram.com/yourhandle"
                    value={formData.contact.socialMedia.instagram}
                    onChange={(e) => handleSocialMediaChange('instagram', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">YouTube</label>
                  <input
                    type="url"
                    className="form-input"
                    placeholder="https://youtube.com/yourchannel"
                    value={formData.contact.socialMedia.youtube}
                    onChange={(e) => handleSocialMediaChange('youtube', e.target.value)}
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">LinkedIn</label>
                <input
                  type="url"
                  className="form-input"
                  placeholder="https://linkedin.com/company/yourcompany"
                  value={formData.contact.socialMedia.linkedin}
                  onChange={(e) => handleSocialMediaChange('linkedin', e.target.value)}
                />
              </div>

              <h3>Contact Form Settings</h3>
              <div className="form-group">
                <label className="form-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.contact.contactForm.enabled}
                    onChange={(e) => handleContactFormChange('enabled', e.target.checked)}
                  />
                  <span>Enable contact form on website</span>
                </label>
              </div>

              {formData.contact.contactForm.enabled && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Recipient Email</label>
                      <input
                        type="email"
                        className="form-input"
                        value={formData.contact.contactForm.recipientEmail}
                        onChange={(e) => handleContactFormChange('recipientEmail', e.target.value)}
                      />
                      <span className="form-hint">Contact form submissions will be sent to this email</span>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Subject Prefix</label>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.contact.contactForm.subjectPrefix}
                        onChange={(e) => handleContactFormChange('subjectPrefix', e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-checkbox">
                      <input
                        type="checkbox"
                        checked={formData.contact.contactForm.autoReplyEnabled}
                        onChange={(e) => handleContactFormChange('autoReplyEnabled', e.target.checked)}
                      />
                      <span>Send auto-reply to form submissions</span>
                    </label>
                  </div>

                  {formData.contact.contactForm.autoReplyEnabled && (
                    <div className="form-group indented">
                      <label className="form-label">Auto-Reply Message</label>
                      <textarea
                        className="form-textarea"
                        rows={3}
                        value={formData.contact.contactForm.autoReplyMessage}
                        onChange={(e) => handleContactFormChange('autoReplyMessage', e.target.value)}
                      />
                    </div>
                  )}
                </>
              )}

              <h3>Additional Contact Emails</h3>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Support Email</label>
                  <input
                    type="email"
                    className="form-input"
                    value={formData.contact.additionalContacts.supportEmail}
                    onChange={(e) => handleAdditionalContactsChange('supportEmail', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Sales Email</label>
                  <input
                    type="email"
                    className="form-input"
                    value={formData.contact.additionalContacts.salesEmail}
                    onChange={(e) => handleAdditionalContactsChange('salesEmail', e.target.value)}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Returns Email</label>
                  <input
                    type="email"
                    className="form-input"
                    value={formData.contact.additionalContacts.returnsEmail}
                    onChange={(e) => handleAdditionalContactsChange('returnsEmail', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">WhatsApp Number</label>
                  <input
                    type="tel"
                    className="form-input"
                    placeholder="+44 7700 900000"
                    value={formData.contact.additionalContacts.whatsapp}
                    onChange={(e) => handleAdditionalContactsChange('whatsapp', e.target.value)}
                  />
                </div>
              </div>
            </div>
          )} */}

          {/* TODO: Uncomment when Security functionality is fully implemented */}
          {/* {activeTab === 'security' && (
            <div className="settings-section">
              <h2>Security Settings</h2>
              <p className="section-description">Manage your account security and two-factor authentication.</p>
              <TwoFactorSettings />
            </div>
          )} */}
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;
